import React, { useState } from 'react'

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import StackedTable from '@/common/components/StackedTable.tsx'
import { cn, getSemanticColor } from '@/common/utilities.ts'
import Badge from '@/common/components/Badge.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import { useQuery } from '@tanstack/react-query'
import { ProgramQueries } from '@/queries/program/queries.ts'
import { getUserListStatusText } from '@/queries/users/requests.ts'
import { useSelector } from 'react-redux'
import { RootState } from '@/storage/store.ts'

interface UserProgramsProps {
  idScroll?: string
  userId?: number
}

const UserPrograms: React.FC<UserProgramsProps> = ({ idScroll, userId }) => {
  const user = useSelector((state: RootState) => state.auth.user)
  const [currentPage, setCurrentPage] = useState(1)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10)
  const startIndex = (currentPage - 1) * recordsPerPage

  const UserPrograms = useQuery(
    ProgramQueries.FetchUserPrograms(userId, { enabled: user?.id != userId })
  )
  const selectedData =
    user && user.id == userId
      ? user?.enrolled_programs
      : UserPrograms?.data?.slice(startIndex, startIndex + recordsPerPage)

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  return (
    <div>
      <Card className="mt-4" id={idScroll}>
        <CardHeader className="flex justify-between items-center text-md font-semibold">
          <div className="text-left text-md font-semibold text-gray-900">
            Programs
          </div>
        </CardHeader>
        <CardContent className="p-0 lg:p-0">
          <StackedTable.Table>
            <StackedTable.Header>
              <th
                scope="col"
                className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
              >
                Name
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                Status
              </th>
            </StackedTable.Header>
            <StackedTable.Body>
              {selectedData && selectedData.length == 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="text-center p-2 px-2 py-3.5 text-sm font-semibold text-gray-900 md:py-4 md:px-2.5"
                  >
                    No programs found
                  </td>
                </tr>
              )}
              {selectedData &&
                selectedData.map((program, planIdx) => (
                  <tr key={planIdx}>
                    <td
                      className={cn(
                        'relative py-4 pl-4 pr-3 text-sm sm:pl-6 flex flex-col gap-px',
                        {
                          'border-t border-gray-200': planIdx !== 0,
                        }
                      )}
                    >
                      {program.name}
                      <span className="text-sm text-gray-500">
                        {program.state_name}
                      </span>
                    </td>
                    <td
                      className={cn('px-3 py-3.5 text-sm text-gray-500', {
                        'border-t border-gray-200': planIdx !== 0,
                      })}
                    >
                      <Badge
                        semanticColor={getSemanticColor(
                          getUserListStatusText(program.status)
                        )}
                      >
                        {getUserListStatusText(program.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
            </StackedTable.Body>
          </StackedTable.Table>
        </CardContent>
        {selectedData && selectedData.length > 0 && (
          <CardFooter className="p-2">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPage}
              totalRows={
                user && user.id == userId
                  ? (user?.enrolled_programs?.length ?? 0)
                  : (UserPrograms?.data?.length ?? 0)
              }
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>
        )}
      </Card>
    </div>
  )
}
export default UserPrograms
